// Per-lot curve prices for the Ingot Curve, computed with the exact @hashcurve/protocol curve math.
//
// Lot i (0-based) covers curve supply [i*LOT, (i+1)*LOT). Its cost is what the contract charges
// to buy that lot when it is the next one: buyCost(sold, reserve, LOT), where the reserve is the
// one reached by buying lots 0..i-1 in order (the constant-product curve is path-independent up
// to wei rounding, so sells in between do not change the silhouette). Fees are not included.
import { LOT, N_LOTS, buyCost, quoteBuy } from '@hashcurve/protocol';

export interface LotPrice {
  /** curve cost of the whole lot in wei (no fee) */
  cost: bigint;
  /** fee on top of the cost, in wei */
  fee: bigint;
  /** wei per whole token: cost / 4,000,000 */
  perToken: number;
}

function build(): LotPrice[] {
  const out: LotPrice[] = [];
  let sold = 0n;
  let reserve = 0n;
  for (let i = 0; i < N_LOTS; i++) {
    const q = quoteBuy(sold, reserve);
    const cost = buyCost(sold, reserve, LOT);
    out.push({ cost, fee: q.fee, perToken: Number(cost) / Number(LOT / 10n ** 18n) });
    sold += LOT;
    reserve += cost;
  }
  return out;
}

export const LOT_PRICES: readonly LotPrice[] = build();

const MAX_COST = Number(LOT_PRICES[N_LOTS - 1]!.cost);

/** Column height of lot i as a share of the tallest (last) lot, in (0, 1]. */
export const lotHeight = (i: number) => Number(LOT_PRICES[Math.min(N_LOTS - 1, Math.max(0, i))]!.cost) / MAX_COST;

/** Sum of every lot's cost: the curve reserve at graduation (before rounding dust). */
export const TOTAL_CURVE_COST = LOT_PRICES.reduce((a, l) => a + l.cost, 0n);
